import React, { useEffect, useRef, useState } from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Div100vh from "react-div-100vh";
import OTPInput from "react-otp-input";

import {
  isValidPhone,
  getDataB2B,
  toAbsoluteJourneyUrl,
  getUserFacingErrorMessage,
} from "@/lib/helper";
import PrimaryButton from "../components/PrimaryButton";
import GoBackButton from "@/components/common/buttons/GoBackButton";
import { verifyOtpB2B, sendOtp, verifyOtp } from "@/lib/apis";
import config from "@/config";
import CustomDrawer from "@/components/common/drawer";
import Header from "../components/Header";

import TextField from "@/components/common/Input/TextField";
import { ChevronLeftIcon } from "lucide-react";
import { getWelcomeSubheading } from "@/b2b/config/partnerJourney";

export default function PartnerLogin({ theme }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userIdentifier } = useParams();

  const { phone: initialPhone, name } = location.state || {};

  const [phone, setPhone] = useState(initialPhone || "");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpDrawer, setOtpDrawer] = useState(false);
  const [error, setError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [newOtpSent, setNewOtpSent] = useState(false);

  const [isChecked, setIsChecked] = useState(true);
  const [linkDrawer, setLinkDrawer] = useState(false);
  const [currLink, setCurrLink] = useState("");

  const [b2CJourneyLink, setB2CJourneyLink] = useState("");

  // Guards against duplicate OTP requests: one for an in-flight request,
  // one so the auto-submit-on-landing effect fires at most once.
  const otpRequestInFlight = useRef(false);
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (otp.length === config.OTP_LENGTH) {
      handleVerifyOtp();
    }
  }, [otp]);

  const handleFormSubmit = async () => {
    // Prevent the OTP API from being hit twice (auto-submit effect + manual
    // "Get OTP" click, or any re-render re-firing the request).
    if (otpRequestInFlight.current || loading) return;
    try {
      otpRequestInFlight.current = true;
      setError("");
      setLoading(true);

      // useParams() is always synchronously correct for this route; localStorage is only a
      // fallback in case this component is ever reused off its usual /p/:userIdentifier mount
      // — see SignUpScreen.jsx for the incident this pattern was built to prevent.
      const response = initialPhone
        ? await getDataB2B(
            "b2b/onboard/verify-token",
            userIdentifier || localStorage.getItem("user_identifier")
          )
        : await sendOtp({ phone, source: theme.name });

      if (response?.status === "success" || response?.success) {
        setOtpDrawer(true);
        if (response.link) {
          setB2CJourneyLink(response.link?.link?.url);
        }
      } else {
        setError(getUserFacingErrorMessage(response));
      }
    } catch (error) {
      console.error("Error in handleFormSubmit:", error);
      setError(
        "Something went wrong on our end. Please try again in a moment — if it keeps happening, contact support."
      );
    } finally {
      setLoading(false);
      otpRequestInFlight.current = false;
    }
  };

  console.log(b2CJourneyLink);
  const userAgent = navigator.userAgent;

  console.log(userAgent);

  useEffect(() => {
    if (autoSubmittedRef.current) return;
    if (isValidPhone(phone) && !otpDrawer) {
      autoSubmittedRef.current = true;
      handleFormSubmit();
    }
  }, [phone]);

  const handleResendOtp = async () => {
    setOtp("");
    setVerifying(true);
    await handleFormSubmit();
    setOtpError("");
    setNewOtpSent(true);
    setVerifying(false);
  };

  const handleVerifyOtp = async () => {
    if (otp.length < config.OTP_LENGTH) {
      setOtpError("Please enter the complete OTP.");
      return;
    }

    try {
      setVerifying(true);
      const response = initialPhone
        ? await verifyOtpB2B({ otp }, "b2b/onboard/verify-otp", userIdentifier)
        : await verifyOtp({
            otp: otp,
          });

      if (response?.status === false || response.success === false) {
        setOtpError("Invalid OTP. Please try again.");
      } else {
        // Named distinctly from the outer useParams()-sourced `userIdentifier` (used above for
        // the auth header) so this localStorage read for the navigate() below can't be confused
        // with, or accidentally shadow, the reliable value — see PartnerSignUp.jsx for the same
        // pattern.
        const storedUserIdentifier = localStorage.getItem("user_identifier");
        if (b2CJourneyLink) {
          // Same guard as PartnerSignUp: b2CJourneyLink is the backend link.url,
          // which is a relative "undefined/b/<id>" when BASE_FE_URL is missing.
          // Absolutize it so the redirect can't 404 on /p/undefined/b/<id>.
          window.location.href = toAbsoluteJourneyUrl(b2CJourneyLink);
          return;
        }
        if (!initialPhone)
          setJourneyToken(response.token);
        // `?step=loading` says "we just authenticated — now work out where this person goes".
        // See the note in PartnerScreen: a bare `/p/<partner>` is a public landing and must not
        // route anyone on the strength of a token left behind by a previous visitor.
        navigate(`/p/${storedUserIdentifier}?step=loading`);
      }
    } catch (error) {
      console.error("Error in handleVerifyOtp:", error);
      setOtpError("Verification failed. Try again.");
    } finally {
      setVerifying(false);
    }
  };

  if (/android/i.test(userAgent)) {
    return (
      <div
        className={`bg-[${theme?.bgColor}] ${theme?.textColor} p-12 flex flex-col justify-between h-screen overflow-y-scroll`}
      >
        <div className="flex flex-col gap-4">
          <Header
            heading={name ? `Hey ${name} 👋🏻` : "Welcomeeee 👋🏻"}
            subheading={getWelcomeSubheading(userIdentifier)}
            showBackButton={false}
          />

          <TextField
            name="phone"
            label="Phone Number"
            value={phone}
            onChange={(val) => setPhone(val)}
            placeholder="Enter your phone number"
            disabled={!!initialPhone}
            type="tel"
            required
            maxLength={10}
          />
        </div>

        {error && (
          <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
            {error}
          </div>
        )}

        <CustomDrawer isOpen={otpDrawer} theme={theme}>
          <div className="p-10">
            <GoBackButton customBackSubmit={() => setOtpDrawer(false)} />
            <div className={`w-full text-left ${theme?.textColor}`}>
              <div className={`heading mb-2 mt-12 ${theme.headingColor}`}>
                OTP Verification
              </div>
              <div className={`subheading text-sm ${theme?.textColor}`}>
                OTP Sent to <span>{phone}</span>
              </div>
            </div>
            <div className="flex-grow overflow-auto mt-5">
              <OTPInput
                value={otp}
                numInputs={config.OTP_LENGTH}
                onChange={setOtp}
                isInputNum={true}
                containerStyle={{ display: "flex", justifyContent: "center" }}
                renderInput={(props) => (
                  <input
                    {...props}
                    type="tel"
                    style={{ border: "1px solid #444", margin: "4px" }}
                    className="border-[#999] border w-10 text-center h-12 text-black text-lg bg-transparent"
                  />
                )}
              />
              {otpError && (
                <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
                  {otpError}
                </div>
              )}
              {!verifying && (
                <div className="w-full p-4 text-center subheading text-sm">
                  <span>
                    {newOtpSent
                      ? "New OTP sent, try again if not received."
                      : "Didn't get the code?"}
                  </span>{" "}
                  <span
                    onClick={handleResendOtp}
                    className="font-semibold cursor-pointer text-[#1987fb]"
                  >
                    Resend it
                  </span>
                </div>
              )}
            </div>
            <div className="pt-6">
              <PrimaryButton
                text="Verify"
                handleClick={handleVerifyOtp}
                loading={verifying}
                disabled={otp.length < config.OTP_LENGTH}
              />
            </div>
          </div>
        </CustomDrawer>

        <CustomDrawer isOpen={linkDrawer} theme={theme}>
          {/* <GoBackButton customBackSubmit={()=>setLinkDrawer(false)}/> */}

          <div className="h-full w-full absolute left-0 top-0">
            <iframe
              src={currLink}
              title="Website Frame"
              className="w-full h-full rounded-xl border border-[#444] my-4"
              allowFullScreen
            ></iframe>
          </div>
          <button
            onClick={() => setLinkDrawer(false)}
            className="absolute top-8 left-8 p-2 cursor-pointer hover:bg-slate-100 se:w-8 se:h-8 sm:h-11 sm:w-11 bg-white rounded-full flex flex-col justify-center items-center"
          >
            <ChevronLeftIcon className="size-112  text-black" />
          </button>
        </CustomDrawer>

        <div className="flex flex-col items-center gap-2 mt-10 h-full justify-end">
          {/* consent */}
          <div className="flex items-start space-x-2 text-3xs mb-4">
            {/* Checkbox */}

            {/* Label */}
            <label
              htmlFor="termsCheckbox"
              className=" text-[#999]  leading-relaxed"
            >
              {/* If you are chaning below consent, change this as well: CONSENT_MESSAGE */}
              By continuing, I hereby agree to{" "}
              <a
                // href="/terms-and-conditions"
                // target="_blank"
                // rel="noopener noreferrer"
                onClick={() => {
                  setCurrLink("https://www.circlepe.club/terms-conditions");
                  setLinkDrawer(true);
                }}
                className="underline hover:text-primary cursor-pointer"
              >
                CirclePe's T&Cs
              </a>{" "}
              and{" "}
              <a
                // href="/privacy-policy"
                // target="_blank"
                // rel="noopener noreferrer"
                onClick={() => {
                  setCurrLink("https://www.circlepe.club/privacy-policy");
                  setLinkDrawer(true);
                }}
                className="underline hover:text-primary cursor-pointer"
              >
                Privacy Policy
              </a>{" "}
              & consent to be contacted by CirclePe and its partners via
              whatsapp, etc.
            </label>
          </div>
        </div>

        <PrimaryButton
          text="Get OTP"
          handleClick={handleFormSubmit}
          loading={loading}
          disabled={!phone || loading || otpDrawer}
        />

        <div className="w-full text-center text-[0.678rem] mt-2">
          powered by{" "}
          <a
            target="_blank"
            href="https://fexprime.com"
            className="hover:underline hover:text-primary"
          >
            Fexprime
          </a>
        </div>
      </div>
    );
  } else {
    return (
      <Div100vh
        className={`bg-[${theme?.bgColor}] ${theme?.textColor} p-8 flex flex-col justify-between`}
      >
        <div className="flex flex-col gap-4">
          <Header
            heading={name ? `Hey ${name} 👋🏻` : "Welcomeeee 👋🏻"}
            subheading={getWelcomeSubheading(userIdentifier)}
            showBackButton={false}
          />

          <TextField
            name="phone"
            label="Phone Number"
            value={phone}
            onChange={(val) => setPhone(val)}
            placeholder="Enter your phone number"
            disabled={!!initialPhone}
            type="tel"
            required
            maxLength={10}
          />
        </div>

        {error && (
          <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
            {error}
          </div>
        )}

        <CustomDrawer isOpen={otpDrawer} theme={theme}>
          <div className="p-10">
            <GoBackButton customBackSubmit={() => setOtpDrawer(false)} />
            <div className={`w-full text-left ${theme?.textColor}`}>
              <div className={`heading mb-2 mt-12 ${theme.headingColor}`}>
                OTP Verification
              </div>
              <div className={`subheading text-sm ${theme?.textColor}`}>
                OTP Sent to <span>{phone}</span>
              </div>
            </div>
            <div className="flex-grow overflow-auto mt-5">
              <OTPInput
                value={otp}
                numInputs={config.OTP_LENGTH}
                onChange={setOtp}
                isInputNum={true}
                containerStyle={{ display: "flex", justifyContent: "center" }}
                renderInput={(props) => (
                  <input
                    {...props}
                    type="tel"
                    style={{ border: "1px solid #444", margin: "4px" }}
                    className="border-[#999] border w-10 text-center h-12 text-black text-lg bg-transparent"
                  />
                )}
              />
              {otpError && (
                <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
                  {otpError}
                </div>
              )}
              {!verifying && (
                <div className="w-full p-4 text-center subheading text-sm">
                  <span>
                    {newOtpSent
                      ? "New OTP sent, try again if not received."
                      : "Didn't get the code?"}
                  </span>{" "}
                  <span
                    onClick={handleResendOtp}
                    className="font-semibold cursor-pointer text-[#1987fb]"
                  >
                    Resend it
                  </span>
                </div>
              )}
            </div>
            <div className="pt-6">
              <PrimaryButton
                text="Verify"
                handleClick={handleVerifyOtp}
                loading={verifying}
                disabled={otp.length < config.OTP_LENGTH}
              />
            </div>
          </div>
        </CustomDrawer>

        <CustomDrawer isOpen={linkDrawer} theme={theme}>
          {/* <GoBackButton customBackSubmit={()=>setLinkDrawer(false)}/> */}

          <div className="h-full w-full absolute left-0 top-0">
            <iframe
              src={currLink}
              title="Website Frame"
              className="w-full h-full rounded-xl border border-[#444] my-4"
              allowFullScreen
            ></iframe>
          </div>
          <button
            onClick={() => setLinkDrawer(false)}
            className="absolute top-8 left-8 p-2 cursor-pointer hover:bg-slate-100 se:w-8 se:h-8 sm:h-11 sm:w-11 bg-white rounded-full flex flex-col justify-center items-center"
          >
            <ChevronLeftIcon className="size-112  text-black" />
          </button>
        </CustomDrawer>

        <div className="flex flex-col items-center gap-2 mt-10 h-full justify-end">
          {/* consent */}
          <div className="flex items-start space-x-2 text-3xs mb-4">
            {/* Checkbox */}

            {/* Label */}
            <label
              htmlFor="termsCheckbox"
              className=" text-[#999]  leading-relaxed"
            >
              {/* If you are chaning below consent, change this as well: CONSENT_MESSAGE */}
              By continuing, I hereby agree to{" "}
              <a
                // href="/terms-and-conditions"
                // target="_blank"
                // rel="noopener noreferrer"
                onClick={() => {
                  setCurrLink("https://www.circlepe.club/terms-conditions");
                  setLinkDrawer(true);
                }}
                className="underline hover:text-primary cursor-pointer"
              >
                CirclePe's T&Cs
              </a>{" "}
              and{" "}
              <a
                // href="/privacy-policy"
                // target="_blank"
                // rel="noopener noreferrer"
                onClick={() => {
                  setCurrLink("https://www.circlepe.club/privacy-policy");
                  setLinkDrawer(true);
                }}
                className="underline hover:text-primary cursor-pointer"
              >
                Privacy Policy
              </a>{" "}
              & consent to be contacted by CirclePe and its partners via
              whatsapp, etc.
            </label>
          </div>
        </div>

        <PrimaryButton
          text="Get OTP"
          handleClick={handleFormSubmit}
          loading={loading}
          disabled={!phone || loading || otpDrawer}
        />

        <div className="w-full text-center text-[0.678rem] mt-2">
          powered by{" "}
          <a
            target="_blank"
            href="https://fexprime.com"
            className="hover:underline hover:text-primary"
          >
            Fexprime
          </a>
        </div>
      </Div100vh>
    );
  }
}
